import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { formatCurrency, cn } from "@/lib/utils";
import { getPanelBasePath } from "@/lib/authz";
import {
  ShoppingCart, TrendingUp, Clock, AlertTriangle,
  ArrowRight, ChevronRight,
} from "lucide-react";
import { StatCardsSkeleton, ChartSkeleton, ListItemSkeleton } from "@/components/common/Skeletons";
import SmartAlerts from "@/components/SmartAlerts";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const emptyStats = {
  total_orders: 0, pending_orders: 0, shipped_orders: 0,
  total_revenue: 0, total_products: 0, low_stock_count: 0,
  orders_today: 0, revenue_today: 0,
};

function pctColor(v) {
  if (v == null) return "";
  return v >= 0 ? "text-emerald-500" : "text-rose-500";
}

function PctPill({ pct }) {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 tabular-nums",
      up ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-500"
    )}>
      {up ? "▲" : "▼"} {Math.abs(pct)}%
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    delivered:       { label: "Teslim",    cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
    processing:      { label: "İşlemde",   cls: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
    pending:         { label: "Bekliyor",  cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
    preparing:       { label: "Hazırlanıyor", cls: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400" },
    payment_received:{ label: "Ödendi",    cls: "bg-teal-500/10 text-teal-600 dark:text-teal-400" },
    shipped:         { label: "Kargoda",   cls: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400" },
    in_transit:      { label: "Kargoda",   cls: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400" },
    cancelled:       { label: "İptal",     cls: "bg-zinc-500/10 text-zinc-500" },
  };
  const k = String(status || "").toLowerCase();
  const m = map[k] || { label: status || "—", cls: "bg-zinc-500/10 text-zinc-500" };
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", m.cls)}>
      {m.label}
    </span>
  );
}

function ChannelLogo({ channel, size = 7 }) {
  const c = String(channel || "").toLowerCase();
  return (
    <div className={cn(
      `w-${size} h-${size} rounded-lg bg-white dark:bg-white/10 border border-border`,
      "flex items-center justify-center overflow-hidden shrink-0"
    )}>
      <img
        src={`/integrations/${c}.svg`}
        alt={c}
        className="w-4 h-4 object-contain"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    </div>
  );
}

function displayOrderNo(v) {
  const s = String(v || "").trim();
  if (!s) return "#—";
  let n = s.includes(" - ") ? s.split(" - ")[0].trim() : s;
  if (n.includes("_") && /^\d+$/.test(n.split("_")[0])) n = n.split("_")[0];
  return `#${n.replace(/^(TY-|WC-|HB-|AZ-)/i, "").replace(/^#/, "")}`;
}

/* channel colour palette */
const CHANNEL_COLORS = [
  { bg: "bg-[#1487a3]/10", ring: "ring-[#1487a3]/30", text: "text-[#1487a3]", bar: "#1487a3" },
  { bg: "bg-orange-500/10", ring: "ring-orange-400/30", text: "text-orange-500", bar: "#f97316" },
  { bg: "bg-violet-500/10", ring: "ring-violet-400/30", text: "text-violet-500", bar: "#8b5cf6" },
  { bg: "bg-emerald-500/10", ring: "ring-emerald-400/30", text: "text-emerald-500", bar: "#10b981" },
  { bg: "bg-rose-500/10",   ring: "ring-rose-400/30",   text: "text-rose-500",   bar: "#f43f5e" },
];


/* ─── sub-components ───────────────────────────────────────────────────────── */

function KpiCard({ title, value, sub, pct, icon: Icon, iconBg, iconColor, accentBar, dark, loading, onClick }) {
  const labelColor = dark ? "rgba(180,210,230,0.58)" : undefined;
  const valueColor = dark ? "#ffffff" : undefined;
  const subColor   = dark ? "rgba(180,210,230,0.46)" : undefined;

  return (
    <button
      onClick={onClick}
      className={cn(
        "group relative w-full text-left rounded-2xl overflow-hidden",
        "transition-all duration-200 hover:-translate-y-[2px]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        dark
          ? "border border-white/[0.13] hover:border-white/[0.22]"
          : "bg-card border border-border/70 shadow-[0_1px_3px_rgba(8,38,60,0.05),0_4px_18px_rgba(8,38,60,0.08)] hover:shadow-[0_8px_32px_rgba(8,38,60,0.14)]"
      )}
      style={dark ? { background: "rgba(255,255,255,0.07)", backdropFilter: "blur(14px)" } : undefined}
    >
      {/* Colored top accent bar */}
      <div className="h-[3px] w-full" style={{ background: accentBar }} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <p className={cn("text-[10px] font-bold uppercase tracking-[0.12em]", !dark && "text-muted-foreground")}
             style={labelColor ? { color: labelColor } : undefined}>
            {title}
          </p>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={dark ? { background: "rgba(255,255,255,0.10)" } : undefined}
          >
            <div className={cn(!dark && iconBg, "w-10 h-10 rounded-xl flex items-center justify-center")}>
              <Icon className={cn("h-5 w-5", iconColor)} strokeWidth={1.75} />
            </div>
          </div>
        </div>
        {loading ? (
          <div className="h-9 w-28 rounded-lg bg-muted/20 animate-pulse mb-2" />
        ) : (
          <div className="flex items-baseline gap-2 flex-wrap">
            <p
              className={cn("text-[34px] font-bold leading-none tracking-tight tabular-nums", !dark && "text-foreground")}
              style={{ fontFamily: "'Manrope', sans-serif", ...(valueColor ? { color: valueColor } : {}) }}
            >
              {value}
            </p>
            <PctPill pct={pct} />
          </div>
        )}
        {sub && (
          <p className={cn("text-xs mt-2.5", !dark && "text-muted-foreground")}
             style={subColor ? { color: subColor } : undefined}>
            {sub}
          </p>
        )}
      </div>
    </button>
  );
}

function ChartCard({ data, loading }) {
  const totalRevenue = data.reduce((s, d) => s + (d.total || 0), 0);
  const totalOrders = data.reduce((s, d) => s + (d.count || 0), 0);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-4">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Satış Grafiği</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Son 7 gün</p>
        </div>
        {!loading && (
          <div className="flex items-center gap-4 text-right">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Ciro</p>
              <p className="text-sm font-bold text-foreground tabular-nums">{formatCurrency(totalRevenue)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Sipariş</p>
              <p className="text-sm font-bold text-foreground tabular-nums">{totalOrders}</p>
            </div>
          </div>
        )}
      </div>
      <div className="h-[220px]">
        {loading ? (
          <ChartSkeleton height={220} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1487a3" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#1487a3" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false} axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false} axisLine={false} width={36}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="rounded-xl bg-card border border-border/80 shadow-lg p-3 text-xs">
                      <p className="font-semibold text-foreground mb-1">
                        {new Date(payload[0].payload.date).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}
                      </p>
                      <p className="text-[#1487a3] font-bold">{formatCurrency(payload[0].value)}</p>
                      <p className="text-muted-foreground">{payload[0].payload.count} sipariş</p>
                    </div>
                  );
                }}
                cursor={{ stroke: "#1487a3", strokeWidth: 1, strokeDasharray: "4 4" }}
              />
              <Area
                type="monotone" dataKey="total"
                stroke="#1487a3" strokeWidth={2}
                fill="url(#gradRevenue)" dot={false}
                activeDot={{ r: 4, fill: "#1487a3", stroke: "hsl(var(--card))", strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function RecentOrdersCard({ orders, loading, navigate, basePath }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Son Siparişler</h2>
          <p className="text-xs text-muted-foreground mt-0.5">En son gelenler</p>
        </div>
        <button
          onClick={() => navigate(`${basePath}/siparisler`)}
          className="flex items-center gap-1 text-xs text-primary hover:underline font-medium"
        >
          Tümü <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-1">
        {loading ? (
          <ListItemSkeleton rows={5} />
        ) : orders.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Henüz sipariş yok.</div>
        ) : (
          orders.map((order) => (
            <div
              key={order.id}
              onClick={() => navigate(`${basePath}/siparisler`)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer",
                "hover:bg-muted/60 transition-colors"
              )}
            >
              <ChannelLogo channel={order.channel} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate leading-tight">
                  {order.customer_name || "—"}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {displayOrderNo(order.channel_order_id || order.id)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-foreground tabular-nums">
                  {formatCurrency(order.total_amount)}
                </p>
                <StatusBadge status={order.status} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ChannelCard({ ch, index, total }) {
  const pct = total > 0 ? Math.round((ch.orders / total) * 100) : 0;
  const palette = CHANNEL_COLORS[index % CHANNEL_COLORS.length];
  const channelName = ch.channel
    ? ch.channel.charAt(0).toUpperCase() + ch.channel.slice(1)
    : "Bilinmeyen";

  return (
    <div className={cn(
      "relative rounded-2xl border border-border bg-card overflow-hidden",
      "transition-all duration-200 hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
    )}>
      {/* Colour accent stripe */}
      <div className="h-[3px] w-full" style={{ backgroundColor: palette.bar }} />

      <div className="p-5 flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white dark:bg-white/10 border border-border/60 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
            <img
              src={`/integrations/${ch.channel}.svg`}
              alt={channelName}
              className="w-5 h-5 object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{channelName}</p>
            <p className="text-[10px] text-muted-foreground">Son 7 gün</p>
          </div>
          <span className={cn(
            "text-[11px] font-bold px-2.5 py-1 rounded-full tabular-nums shrink-0",
            palette.bg, palette.text
          )}>
            %{pct}
          </span>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-2">
          <div className={cn("rounded-xl p-3", palette.bg)}>
            <p className={cn("text-[10px] font-semibold uppercase tracking-wide mb-1", palette.text)}>Sipariş</p>
            <p className={cn("text-xl font-bold tabular-nums", palette.text)}>{ch.orders}</p>
          </div>
          <div className="rounded-xl bg-muted/50 p-3">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Ciro</p>
            <p className="text-sm font-bold text-foreground tabular-nums leading-snug">{formatCurrency(ch.revenue)}</p>
          </div>
        </div>

        {/* Bar */}
        <div>
          <div className="flex justify-between text-[10px] text-muted-foreground mb-1.5">
            <span>Toplam pay</span>
            <span className="tabular-nums font-medium">{pct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${pct}%`, backgroundColor: palette.bar }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const RANK_COLORS = [
  { bg: "bg-amber-500", text: "text-white", bar: "#f59e0b" },
  { bg: "bg-slate-400", text: "text-white", bar: "#94a3b8" },
  { bg: "bg-orange-400", text: "text-white", bar: "#fb923c" },
  { bg: "bg-muted", text: "text-muted-foreground", bar: "#94a3b8" },
  { bg: "bg-muted", text: "text-muted-foreground", bar: "#94a3b8" },
];

function TopProductsCard({ products, loading }) {
  if (loading) return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="h-4 w-36 bg-muted rounded animate-pulse mb-5" />
      <ListItemSkeleton rows={5} />
    </div>
  );

  if (!products?.length) return null;

  const maxQty = Math.max(...products.map(p => p.qty || 0));
  const totalRevenue = products.reduce((s, p) => s + (p.revenue || 0), 0);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">En Çok Satan Ürünler</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Son 7 gün · {products.length} ürün</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Toplam Ciro</p>
          <p className="text-sm font-bold text-foreground tabular-nums">{formatCurrency(totalRevenue)}</p>
        </div>
      </div>

      {/* Top product hero (rank 1) */}
      {(() => {
        const top = products[0];
        const barPct = maxQty > 0 ? Math.round((top.qty / maxQty) * 100) : 100;
        return (
          <div className="mx-4 mb-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200/60 dark:border-amber-900/30 p-3 flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="w-14 h-14 rounded-xl border-2 border-amber-200/80 dark:border-amber-800/40 bg-muted overflow-hidden flex items-center justify-center shadow-sm">
                {top.image_url ? (
                  <img src={top.image_url} alt={top.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-bold text-muted-foreground">{(top.name || "?")[0].toUpperCase()}</span>
                )}
              </div>
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shadow">1</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{top.name || "—"}</p>
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400 tabular-nums">{top.qty} adet</span>
                <span className="text-xs text-muted-foreground tabular-nums">{formatCurrency(top.revenue)}</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full rounded-full bg-amber-200/60 dark:bg-amber-900/30 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400" style={{ width: `${barPct}%` }} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* Remaining products */}
      <div className="divide-y divide-border/50 px-4 pb-3">
        {products.slice(1, 5).map((p, i) => {
          const rank = i + 2;
          const barPct = maxQty > 0 ? Math.round((p.qty / maxQty) * 100) : 0;
          const rc = RANK_COLORS[rank - 1] || RANK_COLORS[3];
          return (
            <div key={rank} className="flex items-center gap-3 py-2.5 group">
              <span className={cn("w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0", rc.bg, rc.text)}>
                {rank}
              </span>
              <div className="w-9 h-9 rounded-lg border border-border bg-muted/50 overflow-hidden shrink-0 flex items-center justify-center">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-bold text-muted-foreground">{(p.name || "?")[0].toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-foreground truncate leading-tight">{p.name || "—"}</p>
                <div className="mt-1 h-1 w-full rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary/60 transition-all duration-700" style={{ width: `${barPct}%` }} />
                </div>
              </div>
              <div className="text-right shrink-0 min-w-[64px]">
                <p className="text-xs font-semibold text-foreground tabular-nums">{p.qty} adet</p>
                <p className="text-[10px] text-muted-foreground tabular-nums">{formatCurrency(p.revenue)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LowStockCard({ items, navigate, basePath }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-2xl border border-rose-200/60 dark:border-rose-900/40 bg-rose-50/50 dark:bg-rose-950/20 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400" />
          </div>
          <h2 className="text-sm font-semibold text-rose-700 dark:text-rose-400">Düşük Stok</h2>
        </div>
        <button
          onClick={() => navigate(`${basePath}/stok`)}
          className="text-xs text-rose-600 dark:text-rose-400 hover:underline font-medium flex items-center gap-1"
        >
          Stok <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      <div className="space-y-2">
        {items.slice(0, 5).map((item, i) => (
          <div key={i} className="flex items-center justify-between">
            <p className="text-xs font-medium text-foreground truncate mr-3">{item.name}</p>
            <span className="text-xs font-bold text-rose-600 dark:text-rose-400 tabular-nums shrink-0">
              {item.stock} adet
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── main component ───────────────────────────────────────────────────────── */

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(emptyStats);
  const [chartData, setChartData] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const basePath = getPanelBasePath(user);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [statsRes, chartRes, ordersRes, overviewRes] = await Promise.allSettled([
          api.get("/dashboard/stats"),
          api.get("/dashboard/sales-chart?days=7"),
          api.get("/dashboard/recent-orders?limit=6"),
          api.get("/dashboard/overview"),
        ]);
        if (statsRes.status === "fulfilled") setStats({ ...emptyStats, ...(statsRes.value || {}) });
        if (chartRes.status === "fulfilled") setChartData(Array.isArray(chartRes.value) ? chartRes.value : []);
        if (ordersRes.status === "fulfilled") setRecentOrders(Array.isArray(ordersRes.value) ? ordersRes.value : []);
        if (overviewRes.status === "fulfilled") setOverview(overviewRes.value || null);
      } catch {} finally {
        setLoading(false);
      }
    })();
  }, []);

  const kpiCards = [
    {
      title: "Bugünkü Sipariş",
      value: loading ? "—" : stats.orders_today,
      sub: `Toplam: ${stats.total_orders.toLocaleString("tr-TR")} sipariş`,
      pct: overview?.change?.orders_pct,
      icon: ShoppingCart,
      iconBg: "bg-[#1487a3]/12",
      iconColor: "text-[#1487a3]",
      accentBar: "linear-gradient(90deg, #1487a3 0%, #22c8e0 100%)",
      path: "/siparisler",
    },
    {
      title: "Bugünkü Ciro",
      value: loading ? "—" : formatCurrency(stats.revenue_today),
      sub: `Toplam: ${formatCurrency(stats.total_revenue)}`,
      pct: overview?.change?.revenue_pct,
      icon: TrendingUp,
      iconBg: "bg-emerald-500/12",
      iconColor: "text-emerald-600",
      accentBar: "linear-gradient(90deg, #059669 0%, #34d399 100%)",
      path: "/raporlar",
    },
    {
      title: "Bekleyen",
      value: loading ? "—" : stats.pending_orders,
      sub: stats.pending_orders > 0 ? "işlem bekliyor" : "temiz",
      icon: Clock,
      iconBg: "bg-amber-500/12",
      iconColor: "text-amber-600",
      accentBar: "linear-gradient(90deg, #d97706 0%, #fbbf24 100%)",
      path: "/siparisler",
    },
    {
      title: "Düşük Stok",
      value: loading ? "—" : stats.low_stock_count,
      sub: stats.low_stock_count > 0 ? "ürün kritik seviyede" : "stok normal",
      icon: AlertTriangle,
      iconBg: "bg-rose-500/12",
      iconColor: "text-rose-600",
      accentBar: "linear-gradient(90deg, #e11d48 0%, #fb7185 100%)",
      path: "/stok",
    },
  ];

  const channelTotal = overview?.channel_breakdown?.reduce((s, c) => s + c.orders, 0) || 0;

  return (
    <div className="w-full" data-testid="dashboard-page">
      <div className="page-shell space-y-6">

        {/* ── Page Header ── */}
        <div>
          <h1 className="text-[30px] font-bold text-foreground tracking-tight leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Hoş geldin, {user?.full_name?.split(" ")[0] || ""}!
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>

        {/* ── Smart Alerts ── */}
        <SmartAlerts />

        {/* ── KPI Row ── */}
        {loading ? (
          <StatCardsSkeleton count={4} />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiCards.map((card) => (
              <KpiCard
                key={card.title}
                {...card}
                loading={loading}
                onClick={() => navigate(`${basePath}${card.path}`)}
              />
            ))}
          </div>
        )}

        {/* ── Chart + Recent Orders ── */}
        <div className="grid gap-5 lg:grid-cols-7">
          <ChartCard data={chartData} loading={loading} />
          <RecentOrdersCard
            orders={recentOrders}
            loading={loading}
            navigate={navigate}
            basePath={basePath}
          />
        </div>

        {/* ── Channel Breakdown ── */}
        {!loading && overview?.channel_breakdown?.length > 0 && (
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold text-foreground">Kanal Dağılımı</h2>
              <span className="text-xs text-muted-foreground">Son 7 gün · {channelTotal} sipariş</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {overview.channel_breakdown.map((ch, i) => (
                <ChannelCard key={ch.channel} ch={ch} index={i} total={channelTotal} />
              ))}
            </div>
          </div>
        )}

        {/* ── Top Products + Low Stock ── */}
        {!loading && overview && (
          <div className="grid gap-5 lg:grid-cols-2">
            <TopProductsCard products={overview.top_products} loading={false} />
            <LowStockCard
              items={overview.low_stock_items}
              navigate={navigate}
              basePath={basePath}
            />
          </div>
        )}

      </div>
    </div>
  );
}
